import { ChangeDetectionStrategy, Component, Optional, input } from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { UserService } from '@fry/lib/users';
import { ProfileStore } from '@fry/lib/profile';
import { UBStep, UnboxedModule } from '@fry/ui';
import { SelectableListMapping } from '@fry/components/common/selectable-list/selectable-list.mapping';
import { FormProviderService } from '@fry/lib/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormDataDialogButtonType, FormDialogComponent } from '@fry/components/common/form';
import {
  ForUserOptions,
  Walkthrough,
  WalkthroughsStore,
  WalkthroughToStepperTranformation
} from '@fry/walkthroughs/lib';
import { BoardCard } from '@fry/boards/lib';
import { FryCommonModule } from '@fry/components/common';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@fry/components/material';

enum MenuIcon {
  Open = 'close',
  Closed = 'more_horiz'
}

@Component({
    selector: 'eas-walkthroughs-card',
    templateUrl: './walkthroughs-card.view.component.html',
    styleUrls: ['./walkthroughs-card.view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MaterialModule,
        RouterModule,
        CommonModule,
        FryCommonModule,
        UnboxedModule,
    ]
})
export class WalkthroughsCardViewComponent {
  static key = 'WalkthroughsCardComponent';
  card = input.required<BoardCard>();

  public menuIcon: MenuIcon;

  public walkthroughs$: Subject<Walkthrough[]>;
  public lastUpdated$ = new Subject<string>();
  public ubSteps: {[key: string]: UBStep[]} = {};
  public allWalkthroughs$ = this.unresolvedWalkthroughs().pipe(
    shareReplay({bufferSize: 1, refCount: false})
  );
  public title = '';

  private apiParams: ForUserOptions = { show_all: false, regenerate: false, unresolved: false };

  public get isShowingAll(): boolean {
    return this.apiParams.show_all;
  }

  private dialogRef: MatDialogRef<FormDialogComponent>;
  private isDestroyed: ReplaySubject<void> = new ReplaySubject<void>(1);

  constructor(
    private profile: ProfileStore,
    private walkthroughsStore: WalkthroughsStore,
    private provider: FormProviderService,
    private dialog: MatDialog,
    @Optional() private userService: UserService
  ) {
    this.walkthroughs$ = new Subject<Walkthrough[]>();
  }

  public ngOnInit() {
    this.title = this.card().options.title || $localize `Exam progress`;
    this.menuClosed();
    this.refresh().subscribe();
  }

  ngOnDestroy(): void {
    this.isDestroyed.next();
    this.isDestroyed.complete();
  }

  //
  // Menu management
  //

  public menuOpened() {
    this.menuIcon = MenuIcon.Open;
  }

  public menuClosed() {
    this.menuIcon = MenuIcon.Closed;
  }

  //
  // Actions
  //
  public refresh(options?: {regenerate?: boolean}) {
    if (options === undefined) {
      options = {};
    }
    const params = { ...this.apiParams, ...options };
    return this.loadWalkthroughs(params).pipe(
      takeUntil(this.isDestroyed),
      tap(walkthroughs => {
        walkthroughs.forEach(walkthrough => {
          this.ubSteps[walkthrough.orig._id] = this.transformForStepper(walkthrough);
        });
        this.walkthroughs$.next(walkthroughs);
      })
    );
  }

  public onRefresh() {
    this.refresh({regenerate: true}).subscribe();
  }

  public onToggleHideWalkthrough(walkthrough: Walkthrough) {
    this.cardUserUsername()
      .pipe(
        switchMap(username => {
          return this.walkthroughsStore.hideWalkthroughForUser(walkthrough, username, !walkthrough.hidden);
        }),
        switchMap(() => {
          return this.loadWalkthroughs(this.apiParams);
        }),
        takeUntil(this.isDestroyed)
      )
      .subscribe(walkthroughs => {
        console.warn('Transformation just before next()');
        this.walkthroughs$.next(walkthroughs);
      });
  }

  public onShowAll() {
    this.apiParams.show_all = !this.apiParams.show_all;
    this.loadWalkthroughs(this.apiParams)
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(walkthroughs => {
        this.walkthroughs$.next(walkthroughs);
      });
  }

  public unresolvedWalkthroughs() {
    return this.cardUserUsername().pipe(
      switchMap(username => {
        return this.walkthroughsStore.getListForUser(username);
      })
    );
  }

  public selectWalkthroughs() {
    if (this.dialogRef) {
      return;
    }

    this.unresolvedWalkthroughs().subscribe(walkthroughs => {
      const ids = walkthroughs.filter(item => !item.state.hidden)
                              .map(item => item.state._id);

      const opts: {id: string, title: string}[] =
        walkthroughs.map(itm => {
          return {
            id: itm.state._id,
            title: itm.walkthrough.walkthrough.title
          };
        });

      const hasWalkthroughs = opts.length > 0;

      let fields = [];
      if (hasWalkthroughs) {
        fields = [
          {
            id: 'states', title: $localize `Select walkthroughs`, initial: ids,
            widget: { type: 'selectableList' },
            options: {
              categories: SelectableListMapping.create([{items: opts, id: 'roles'}]),
              options: { allowsFiltering: false }
            }
          }
        ];
      }

      const model = {};
      this.provider.getFormDataWith(fields, model).subscribe(
        form => {
          const dialogAllowedButtons: FormDataDialogButtonType[] = ['cancel'];
          if (hasWalkthroughs) { dialogAllowedButtons.push('submit'); }

          const dialogTitle = hasWalkthroughs
                              ? $localize `Choose what to show`
                              : $localize `No items to show`;

          this.dialogRef = this.dialog.open(FormDialogComponent, {
            data: {
              title: dialogTitle,
              form,
              visibleButtons: dialogAllowedButtons,
              onSubmit: (value: any) => {
                const visibility = opts.map(itm => {
                  return {id: itm.id, hidden: !value.states.includes(itm.id)};
                });

                return this.cardUserUsername().pipe(
                  switchMap(username => {
                    const mapped = visibility.map(itm => {
                      return this.walkthroughsStore.hideWalkthroughStateForUser(itm.id, username, itm.hidden);
                    });
                    return forkJoin(mapped);
                  }),
                  switchMap(() => this.refresh())
                );
              }
            }
          });
          this.dialogRef.afterClosed().subscribe(
            () => {},
            () => {},
            () => { this.dialogRef = undefined; }
          );

      });
    });
  }

  //
  // Template helpers
  //
  public transformForStepper(walkthrough: Walkthrough): UBStep[] {
    return WalkthroughToStepperTranformation.transform(walkthrough);
  }

  //
  // Private helpers
  //

  private cardUserUsername(): Observable<string> {
    if (this.userService) {
      return of(this.userService.user.doc.username);
    }

    return this.profile.getCurrent().pipe(map(profile => profile.doc.username));
  }

  private loadWalkthroughs(params: ForUserOptions): Observable<Walkthrough[]> {
    return this.cardUserUsername().pipe(
      switchMap(username => {
        return this.walkthroughsStore.getForUser(username, params);
      }),
      tap(walkthroughs => this.updateLastUpdated(walkthroughs))
    );
  }

  private updateLastUpdated(walkthroughs: Walkthrough[]) {
    if (walkthroughs.length === 0) {
      this.lastUpdated$.next('');
      return;
    }
    const timestamp = walkthroughs.map(itm => itm.generatedAt).reduce((acc, cur) => (acc < cur ? cur : acc));
    this.lastUpdated$.next(timestamp);
  }
}
